import { BoxxGenericState, getGenericInitialState } from '@boxx/core';
import { Action, createReducer, on } from '@ngrx/store';
import {
  IContactsStateError,
  IContactsStateSuccess,
} from '../core/IStateErrorSuccess';
import { ContactInteractionModel, ContactModel } from '../models/contact.model';
import * as fromActions from './contact.actions';

export interface ContactState
  extends BoxxGenericState<
    ContactModel[],
    IContactsStateSuccess,
    IContactsStateError
  > {
  filteredItems: ContactModel[];
  selectedId: number;
  interactions: BoxxGenericState<ContactInteractionModel[], any, any>;
}

const initialState: ContactState = {
  ...getGenericInitialState(),
  data: [],
  filteredItems: [],
  selectedId: null,
  interactions: {
    ...getGenericInitialState(),
    data: [],
  },
};

const reducer = createReducer(
  initialState,
  // On Begin Actions
  on(
    fromActions.fetchContactBeginAction,
    fromActions.createContactBeginAction,
    fromActions.deleteContactBeginAction,
    fromActions.updateContactBeginAction,
    fromActions.importContactBeginAction,
    (state): ContactState => ({
      ...state,
      isLoading: true,
      error: null,
      success: null,
    })
  ),

  on(
    fromActions.fetchInteractionsBeginAction,
    (state): ContactState => ({
      ...state,
      interactions: {
        ...state.interactions,
        isLoading: true,
        data: [],
        error: null,
        success: null,
      },
    })
  ),

  // ON Fail Actions
  on(
    fromActions.fetchContactFailAction,
    fromActions.createContactFailAction,
    fromActions.deleteContactFailAction,
    fromActions.updateContactFailAction,
    fromActions.importContactFailAction,
    (state, action): ContactState => ({
      ...state,
      isLoading: false,
      error: { after: getErrorActionType(action.type), error: action.errors },
    })
  ),

  on(
    fromActions.fetchInteractionsFailAction,
    (state, action): ContactState => ({
      ...state,
      interactions: {
        ...state.interactions,
        isLoading: false,
        error: { after: getErrorActionType(action.type), error: action.errors },
      },
    })
  ),
  on(
    fromActions.createInteractionFailAction,
    (state, action): ContactState => ({
      ...state,
      interactions: {
        ...state.interactions,
        isLoading: false,
        error: { after: getErrorActionType(action.type), error: action.errors },
      },
    })
  ),

  // ON Success Actions:
  // FETCH
  on(
    fromActions.fetchContactSuccessAction,
    (state, action): ContactState => ({
      ...state,
      isLoading: false,
      data: action.contacList,
      fetched: true,
      success: { after: getSuccessActionType(action.type) },
    })
  ),

  on(
    fromActions.fetchInteractionsSuccessAction,
    (state, action): ContactState => ({
      ...state,
      interactions: {
        ...state.interactions,
        isLoading: false,
        data: action.interactions,
      },
    })
  ),
  on(
    fromActions.createInteractionSuccessAction,
    (state, action): ContactState => ({
      ...state,
      interactions: {
        ...state.interactions,
        isLoading: false,
        data: action.interactions,
      },
    })
  ),

  // INSERT Contacts:
  on(
    fromActions.createContactSuccessAction,
    (state, action): ContactState => ({
      ...state,
      isLoading: false,
      data: action.contacts,
      success: { after: getSuccessActionType(action.type) },
    })
  ),
  on(
    fromActions.importContactSuccessAction,
    (state, action): ContactState => ({
      ...state,
      isLoading: false,
      data: action.contactList,
      success: { after: getSuccessActionType(action.type) },
    })
  ),

  // REMOVES Contacts
  on(
    fromActions.deleteContactSuccessAction,
    (state, action): ContactState => ({
      ...state,
      isLoading: false,
      data: action.contactList,
      success: { after: getSuccessActionType(action.type) },
    })
  ),

  // UPDATES Contacts
  on(
    fromActions.updateContactSuccessAction,
    (state, action): ContactState => ({
      ...state,
      isLoading: false,
      data: action.contactList,
      success: { after: getSuccessActionType(action.type) },
    })
  ),

  // FILTER
  on(
    fromActions.filterContactsSuccessAction,
    (state, action): ContactState => ({
      ...state,
      filteredItems: action.contactList,
      isLoading: false,
      error: null,
      success: null,
    })
  ),

  // SELECT
  on(
    fromActions.selectContactAction,
    (state, action): ContactState => ({
      ...state,
      selectedId: action.contactId,
      error: null,
      success: null,
    })
  )
);

const getErrorActionType = (type: fromActions.ContactsActionTypes) => {
  let action:
    | 'GET'
    | 'GET_INTERACTIONS'
    | 'CREATE'
    | 'UPDATE'
    | 'DELETE'
    | 'IMPORT'
    | 'UNKNOWN' = 'UNKNOWN';

  switch (type) {
    case fromActions.ContactsActionTypes.fetchContactFail:
      action = 'GET';
      break;
    case fromActions.ContactsActionTypes.fetchInteractionsFail:
      action = 'GET_INTERACTIONS';
      break;
    case fromActions.ContactsActionTypes.createContactFail:
    case fromActions.ContactsActionTypes.createInteractionFail:
      action = 'CREATE';
      break;
    case fromActions.ContactsActionTypes.updateContactFail:
      action = 'UPDATE';
      break;
    case fromActions.ContactsActionTypes.deleteContactFail:
      action = 'DELETE';
      break;
    case fromActions.ContactsActionTypes.importContactFail:
      action = 'IMPORT';
      break;
  }

  return action;
};

const getSuccessActionType = (type: fromActions.ContactsActionTypes) => {
  let action: 'GET' | 'CREATE' | 'UPDATE' | 'DELETE' | 'IMPORT' | 'UNKNOWN' =
    'UNKNOWN';

  switch (type) {
    case fromActions.ContactsActionTypes.fetchContactSuccess:
      action = 'GET';
      break;
    case fromActions.ContactsActionTypes.createContactSuccess:
      action = 'CREATE';
      break;
    case fromActions.ContactsActionTypes.updateContactSuccess:
      action = 'UPDATE';
      break;
    case fromActions.ContactsActionTypes.deleteContactSuccess:
      action = 'DELETE';
      break;
    case fromActions.ContactsActionTypes.importContactSuccess:
      action = 'IMPORT';
      break;
  }

  return action;
};

export const contactsReducer = (
  state: ContactState | undefined,
  action: Action
) => reducer(state, action);
